import {
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	calculateMarginUSDCRequiredForTrade,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@drift-labs/sdk';
import { useMemo } from 'react';
import { ZERO_QUOTE_BIGNUM } from 'src/constants/math';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useDriftStore, {
	DEFAULT_MARKET_CONFIG,
} from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useHighLeverageSpotsOpen from 'src/hooks/useHighLeverageSpotsOpen';
import useMarketMaxLeverage from 'src/hooks/useMarketMaxLeverages';
import { MarketType } from '@drift-labs/sdk';
import useGetDisplayLeverageForMarket from 'src/hooks/useGetDisplayLeverageForMarket';
import {
	COMMON_UI_UTILS,
	computeIsolatedPositionDepositForTrade,
	getTotalIsolatedMarginShortfall,
	UIMarket,
} from '@drift/common';
import { ISOLATED_POSITION_DEPOSIT_BUFFER_DENOMINATOR } from 'src/constants/trade';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';

interface Params {
	/**
	 * Pulls from `tradeForm.baseSizeStringValue` if not provided.
	 */
	baseSizeString?: string;
	/**
	 * Defaults to `0` if not provided.
	 */
	marketIndex?: number;
	/**
	 * Pulls from `tradeForm.priceImpact.entryPrice` if not provided.
	 */
	estEntryPrice?: BN;
}
/**
 * Determines how much USDC is required for the trade.
 */
export const useCollateralValueRequiredForTrade = ({
	marketIndex = 0,
	baseSizeString: baseSizeStringFromProps,
	estEntryPrice: estEntryPriceFromProps,
}: Params = {}) => {
	const baseSizeStringFromStore = useDriftStore(
		(s) => s.tradeForm.baseSizeStringValue
	);
	const baseSizeString = baseSizeStringFromProps ?? baseSizeStringFromStore;

	const estEntryPriceFromStore = useDriftStore(
		(s) => s.tradeForm.priceImpact?.entryPrice
	);
	const estEntryPrice = estEntryPriceFromProps ?? estEntryPriceFromStore;

	const driftClient = useDriftStore((s) => s.driftClient.client);

	const driftClientIsReady = useDriftClientIsReady();

	// High leverage mode logic
	const { highLeverageSpotsOpen } = useHighLeverageSpotsOpen();
	const { hasHighLeverage: isHighLeverageMarket } = useMarketMaxLeverage(
		MarketType.PERP,
		marketIndex
	);

	const canEnterHighLeverageMode = useMemo(() => {
		return (
			!isNaN(highLeverageSpotsOpen) &&
			highLeverageSpotsOpen > 0 &&
			isHighLeverageMarket
		);
	}, [highLeverageSpotsOpen, isHighLeverageMarket]);

	const currentUserAccount = useDriftAccountStore((s) =>
		s.getCurrentUserAccount()
	);

	const userIsInHighLeverageMode =
		currentUserAccount?.client?.isHighLeverageMode('Initial') ?? false;

	// Calculate display leverage for margin ratio (same logic as getMaxTradeSizeUSDCForPerp)
	const displayLeverage = useGetDisplayLeverageForMarket(marketIndex);
	const maxMarginRatio =
		COMMON_UI_UTILS.convertLeverageToMarginRatio(displayLeverage);

	const uiMarket = UIMarket.createPerpMarket(marketIndex);
	const marginMode = useMarketSettingsStore((s) =>
		s.getMarginModeForMarket(uiMarket.key, currentUserAccount?.userKey ?? '')
	);

	const baseSizeBn = useMemo(
		() => BigNum.fromPrint(baseSizeString, BASE_PRECISION_EXP).val,
		[baseSizeString]
	);

	const isolatedPositionDeposit = useMemo(() => {
		if (
			marginMode === 'isolated' &&
			driftClient &&
			currentUserAccount?.client
		) {
			return (
				computeIsolatedPositionDepositForTrade({
					driftClient,
					user: currentUserAccount.client,
					marketIndex:
						uiMarket?.marketIndex ?? DEFAULT_MARKET_CONFIG.marketIndex,
					baseAssetAmount: baseSizeBn,
					marginRatio:
						COMMON_UI_UTILS.convertLeverageToMarginRatio(
							displayLeverage ?? 1
						) ?? 1,
					numOfOpenHighLeverageSpots: highLeverageSpotsOpen,
					bufferDenominator: ISOLATED_POSITION_DEPOSIT_BUFFER_DENOMINATOR,
					includeExistingShortfall: true,
				}) ?? undefined
			);
		}
	}, [
		baseSizeBn,
		uiMarket?.marketIndex,
		marginMode,
		displayLeverage,
		highLeverageSpotsOpen,
	]);

	// Total shortfall from other isolated positions (~6.67% buffer via ISOLATED_POSITION_DEPOSIT_BUFFER_DENOMINATOR)
	const otherIsolatedShortfallsTotal = useMemo(() => {
		if (!currentUserAccount?.client) {
			return ZERO;
		}
		const rawShortfall = getTotalIsolatedMarginShortfall(
			currentUserAccount.client,
			uiMarket?.marketIndex
		);
		// Add buffer on top of shortfall (same as new margin; denominator 15 ≈ 6.67%)
		return rawShortfall.add(
			rawShortfall.div(new BN(ISOLATED_POSITION_DEPOSIT_BUFFER_DENOMINATOR))
		);
	}, [currentUserAccount?.client, uiMarket?.marketIndex]);

	const collateralRequiredBigNum = useMemo(() => {
		if (!driftClientIsReady || !driftClient) {
			return ZERO_QUOTE_BIGNUM;
		}

		if (marginMode === 'isolated') {
			// For isolated mode, include the deposit + other shortfalls
			const baseDeposit = isolatedPositionDeposit ?? ZERO;
			const totalRequired = baseDeposit.add(otherIsolatedShortfallsTotal);
			return BigNum.from(totalRequired, QUOTE_PRECISION_EXP);
		}

		// For cross mode, include the margin required + any isolated shortfalls
		const collateralRequiredBn = calculateMarginUSDCRequiredForTrade(
			driftClient,
			marketIndex,
			baseSizeBn,
			maxMarginRatio,
			canEnterHighLeverageMode || userIsInHighLeverageMode,
			estEntryPrice
		);

		const totalRequired = collateralRequiredBn.add(
			otherIsolatedShortfallsTotal
		);
		return BigNum.from(totalRequired, QUOTE_PRECISION_EXP);
	}, [
		baseSizeBn,
		driftClient,
		estEntryPrice,
		driftClientIsReady,
		canEnterHighLeverageMode,
		userIsInHighLeverageMode,
		marketIndex,
		maxMarginRatio,
		isolatedPositionDeposit,
		otherIsolatedShortfallsTotal,
	]);

	return collateralRequiredBigNum;
};
